import Select, { StylesConfig } from 'react-select';
import moment from 'moment';
import { ShowToast } from '../common/Toastr';
import { updatePageIdState, getPagePermissionsAsync, getManageTerminologyList } from '../common/GlobalSlice';
import { parse, isValid, parseISO, addMinutes } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import fetchAPI from './fetchAPI';
import { t } from 'i18next';

export const getPageSizeArray = () => {
    return [{ value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '30', label: '30' },
    { value: '40', label: '40' },
    { value: '50', label: '50' }];
}

export const setItemStatus = (status: string) => {
    if (status == "Started") {
        return "badge-light-primary"
    } else if (status == "OnHold" || status == "Due" || status == "Late") {
        return "badge-light-warning"
    } else if (status == "In Progress" || status == "Start Soon") {
        return "badge-light-info"
    }
    else if (status == "Completed" || status == "Active" || status == "Soon Due" || status == "Success") {
        return "badge-light-success"
    }
    else if (status == "InActive") {
        return "badge-light"
    }
    else if (status == "Overdue" || status == "Fail") {
        return "badge-light-danger"
    } else if (status === 'Submitted') {
        return "badge-light-primary"
    } else if (status === 'Approved') {
        return "badge-light-success"
    } else if (status === 'Rejected') {
        return "badge-light-danger"
    } else if (status === 'Not Submitted') {
        return "badge-light-warning"
    }
    return "badge-light"
}

export const setActiveStatus = (status: string) => {
    if (status == "Active") {
        return "badge-light-success"
    } else if (status == "InActive") {
        return "badge-light"
    } 
}

export const setMilestoneStatus = (milestonDate: string, completed: string) => {
    if (completed == "Yes") {
        return { class: "badge-light-success", text: "Completed" }
    } else if (moment(new Date()).format('YYYY-MM-DD') > moment(milestonDate).format('YYYY-MM-DD')) {
        return { class: "badge-light-danger", text: "Overdue" }
    } else if (moment(new Date()).format('YYYY-MM-DD') == moment(milestonDate).format('YYYY-MM-DD')) {
        return { class: "badge-light-warning", text: "Due" }
    }
    else if (moment(new Date()).format('YYYY-MM-DD') < moment(milestonDate).format('YYYY-MM-DD')) {
        return { class: "badge-light-primary", text: "Start Soon" }
    }
}

export const getFilteredList = (List: any, searchTerm: any) => {
    return List.filter((item: any) => {
        const searchTermLowerCase = searchTerm.toLowerCase();
        return Object.values(item).some((value: any) => {
            if (typeof value === 'string') {
                // Check if value is a date string
                if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/)) {
                    // Convert date string to Date object
                    const dateValue = new Date(value);
                    // Check if date is valid
                    if (!isNaN(dateValue.getTime())) {
                        // Format date as needed for comparison
                        const formattedDate = dateValue.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'numeric', // Use 'numeric' for 1-digit month
                            day: 'numeric', // Use 'numeric' for 1-digit day
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false, // Use 24-hour format
                            // year: 'numeric',
                            // month: '2-digit',
                            // day: '2-digit',
                        });
                        return formattedDate.toLowerCase().includes(searchTermLowerCase);
                    }
                } else {
                    // Preprocess the search term for date values without leading zeros
                    const processedSearchTerm = value.toLowerCase().replace(/\b(\d{1})\/(\d{1,2})\/(\d{4})\b/g, '0$1/$2/$3');
                    return processedSearchTerm.includes(searchTermLowerCase);
                }
            } else if (typeof value === 'number') {
                return value.toString().includes(searchTermLowerCase);
            }
            return false;
        });
    });
}
export const getNestedFilteredList = (
    list: any,
    searchTerm: string,
    indexOfFirstItem: number,
    indexOfLastItem: number
): any => {
    const searchTermLowerCase = searchTerm.toLowerCase();

    // Helper function to recursively search through nested structures
    const containsSearchTerm = (item: any): boolean => {
        if (Array.isArray(item)) {
            return item.some(element => containsSearchTerm(element));
        } else if (typeof item === 'object' && item !== null) {
            return Object.values(item).some(value => containsSearchTerm(value));
        } else if (typeof item === 'string') {
            if (item.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/)) {
                const dateValue = new Date(item);
                if (!isNaN(dateValue.getTime())) {
                    const formattedDate = dateValue.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    return formattedDate.toLowerCase().includes(searchTermLowerCase);
                }
            } else {
                const processedSearchTerm = item.toLowerCase().replace(/\b(\d{1})\/(\d{1,2})\/(\d{4})\b/g, '0$1/$2/$3');
                return processedSearchTerm.includes(searchTermLowerCase);
            }
        } else if (typeof item === 'number') {
            return item.toString().includes(searchTermLowerCase);
        } else if (typeof item === 'boolean') {
            return item.toString().includes(searchTermLowerCase);
        }
        return false;
    };

    // Combine all 'items' arrays from each property
    const combinedItems = Object.entries(list).flatMap(([propertyKey, { items }]: any) =>
        items.map((item: any) => ({ ...item, property: propertyKey }))
    );

    // Filter and slice the combined items based on the search term and index range
    const filteredAndSlicedItems = combinedItems
        .filter(containsSearchTerm)
        .slice(indexOfFirstItem, indexOfLastItem);

    // Reconstruct the filtered structure and retain the existing totals
    const reconstructedList = filteredAndSlicedItems.reduce((acc, item) => {
        const { property: propertyKey, ...rest } = item;

        if (!acc[propertyKey]) {
            acc[propertyKey] = { totals: {}, items: [] };

            // Initialize totals from the original list if available
            if (list[propertyKey] && list[propertyKey].totals) {
                acc[propertyKey].totals = { ...list[propertyKey].totals };
            }
        }

        acc[propertyKey].items.push(rest);
        return acc;
    }, {});

    // Recalculate only the specified totals based on the original list
    Object.keys(reconstructedList).forEach(propertyKey => {
        const data = reconstructedList[propertyKey];

        // Get the list of fields to recalculate from the original totals
        const fieldsToRecalculate = list[propertyKey]?.totals ? Object.keys(list[propertyKey].totals) : [];

        // Initialize totals if they don't exist
        fieldsToRecalculate.forEach(field => {
            data.totals[field] = 0;
            // if (!data.totals[field]) {
            //     data.totals[field] = 0;
            // }
        });

        // Recalculate totals for specified fields
        data.items.forEach((item: any) => {
            fieldsToRecalculate.forEach(field => {
                if (typeof item[field] === 'number') {
                    data.totals[field] += item[field];
                }
            });
        });
    });


    return reconstructedList;
};

export const removePropertyFromArray = (array: any, propertyToRemove: any) => {
    return array.map((obj: any) => {
        // Destructure the object and remove the specified property
        const { [propertyToRemove]: _, ...rest } = obj;
        return rest;
    });
}
export const isGuid = (stringToTest: any) => {
    if (stringToTest[0] === "{") {
        stringToTest = stringToTest.substring(1, stringToTest.length - 1);
    }
    var regexGuid = /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/gi;
    var x = regexGuid.test(stringToTest);
    return x;
};

export const formatDateToTime = (value: any, format = "HH:mm") => {
    return (value != undefined ? moment(value.split("+")[0]).format(format) : value);
    //return (value != undefined ? moment(value.split("+")[0]).format("HH:mm") : value);
}


export const formatDateToTimeShift = (value: any, format = "HH:mm") => {
    if (!value) return value;
    return moment.utc(value, moment.ISO_8601).format(format);
}

export const formatUniversalDateToTime = (value: any, format = "HH:mm") => {
    if (!value) return "00:00";

    const cleanValue = value.split("+")[0].trim();

    const date = moment(cleanValue, [
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DD HH:mm:ss",
        "MM/DD/YYYY hh:mm:ss A",
        "MM/DD/YYYY HH:mm:ss",
        "DD/MM/YYYY hh:mm:ss A",
        "DD/MM/YYYY HH:mm:ss",
        "M/D/YYYY HH:mm:ss",
        "D/M/YYYY HH:mm:ss",
    ], true);

    if (!date.isValid()) {
        const fallback = moment(cleanValue);
        return fallback.isValid() ? fallback.format(format) : "00:00";
    }

    return date.format(format);
};

export const converToJsonDate = (time: any) => {
    var date = moment().format("YYYY-MM-DD");
    return moment(date + ' ' + time).utcOffset(0, true).format('[/Date(]x[)/]');
}

export const headerDropDownStyles: StylesConfig = {
    control: (styles) => ({ ...styles, backgroundColor: 'white', width: 120, minHeight: '36px', height: '36px', padding: '-1px', borderColor: '#EBECF0', marginRight: '0px', fontWeight: '400px', color: '#172B4F' })
}

export const dropDownSelectionStyles: StylesConfig = {
    control: (styles) => ({ ...styles, backgroundColor: 'white', width: '100%', minHeight: '41px', height: '41px' }),
}

export const dropDownActiveInActive: StylesConfig = {
    control: (styles) => ({ ...styles, backgroundColor: 'white', width: '100%', minHeight: '36px', height: '36px' }),
}

export const paginationStyles: StylesConfig = {
    control: (styles) => ({ ...styles, backgroundColor: 'white', width: 75, minHeight: '35px', height: '35px', padding: '-1px', borderColor: '#EBECF0', marginRight: '0px', fontWeight: '400px', color: '#172B4F' })
}

export const dashboardSelectionStyles: StylesConfig = {
    control: (styles) => ({ ...styles, backgroundColor: 'white', width: '100%', minHeight: '34px', height: '34px' }),
}

export const getProfileSettingForDevelopmentMode = () => {
    return {
        "AccountId": 2128,
        "AccountRoleId": 13205,
        "AccountEmployeeId": 81092,
        // "AccountId": 23926,
        // "AccountRoleId": 111254,
        // "AccountEmployeeId": 113875,
        //"CurrentCultureDateFormat": "dd/MM/yyyy",
        "CurrentCultureDateFormat": "M/d/yyyy",
        "AccessToken": "3a76ea57-8135-4b3e-a492-95167e197e88",
        "EmployeeName": "Muhammad Rameez",
        "EmployeeInitials": "MR",
        "CustomFieldsTemplate": [
            {
                "AccountCustomFieldId": "c328323b-400e-48c9-a982-5b2f456ab2d0",
                "AccountId": 2128,
                "MasterCustomDataTypeId": "7e295184-9623-4445-b99f-48f07929dce5",
                "DatabaseFieldName": "CustomField1",
                "CustomFieldName": "Reference",
                "CustomFieldCaption": "Reference",
                "DefaultValue": "1",
                "MaximumLength": null,
                "MinimumValue": null,
                "MaximumValue": null,
                "DropDownOptions": null,
                "IsRequired": true,
                "IsDisabled": false,
                "MasterEntityTypeId": "369ed0fb-d317-4244-9f20-b7d834521e2b",
                "MasterEntityTypeName": "Timesheet",
                "MasterCustomDataTypeName": "Text",
                "MasterEntityValidationGroup": null
            },
            {
                "AccountCustomFieldId": "7b92c362-8ab3-4e9c-a38f-780f3a52e478",
                "AccountId": 2128,
                "MasterCustomDataTypeId": "bacd6824-9011-4c30-864f-0899fefc004f",
                "DatabaseFieldName": "CustomField2",
                "CustomFieldName": "Number Field",
                "CustomFieldCaption": "Number Field",
                "DefaultValue": 5,
                "MaximumLength": 10,
                "MinimumValue": 3,
                "MaximumValue": 10,
                "DropDownOptions": null,
                "IsRequired": true,
                "IsDisabled": false,
                "MasterEntityTypeId": "369ed0fb-d317-4244-9f20-b7d834521e2b",
                "MasterEntityTypeName": "Timesheet",
                "MasterCustomDataTypeName": "Number",
                "MasterEntityValidationGroup": null
            },
            {
                "AccountCustomFieldId": "9e1e84ab-8940-4e6f-b403-9bc670d7714c",
                "AccountId": 2128,
                "MasterCustomDataTypeId": "7e295184-9623-4445-b99f-48f07929dce5",
                "DatabaseFieldName": "CustomField3",
                "CustomFieldName": "1",
                "CustomFieldCaption": "1",
                "DefaultValue": "1",
                "MaximumLength": 1,
                "MinimumValue": null,
                "MaximumValue": null,
                "DropDownOptions": null,
                "IsRequired": true,
                "IsDisabled": true,
                "MasterEntityTypeId": "369ed0fb-d317-4244-9f20-b7d834521e2b",
                "MasterEntityTypeName": "Timesheet",
                "MasterCustomDataTypeName": "Text",
                "MasterEntityValidationGroup": null
            },
            {
                "AccountCustomFieldId": "0e235aa7-866a-49c9-b71f-20ecdb71bda9",
                "AccountId": 2128,
                "MasterCustomDataTypeId": "574ded9c-7ad4-4649-8b39-d6741dd0ddca",
                "DatabaseFieldName": "CustomField4",
                "CustomFieldName": "Country",
                "CustomFieldCaption": "Country",
                "DefaultValue": "India",
                "MaximumLength": null,
                "MinimumValue": null,
                "MaximumValue": null,
                "DropDownOptions": "Pakistan+India",
                "IsRequired": true,
                "IsDisabled": false,
                "MasterEntityTypeId": "369ed0fb-d317-4244-9f20-b7d834521e2b",
                "MasterEntityTypeName": "Timesheet",
                "MasterCustomDataTypeName": "Drop-Down",
                "MasterEntityValidationGroup": null
            },
            {
                "AccountCustomFieldId": "da5ba4a1-6795-4688-bb83-04d1d9fbcaa3",
                "AccountId": 2128,
                "MasterCustomDataTypeId": "5942a0be-e7fe-4abb-b96f-f0b20d744ce3",
                "DatabaseFieldName": "CustomField5",
                "CustomFieldName": "Date Field",
                "CustomFieldCaption": "Date Field",
                "DefaultValue": "",
                "MaximumLength": null,
                "MinimumValue": null,
                "MaximumValue": null,
                "DropDownOptions": null,
                "IsRequired": true,
                "IsDisabled": false,
                "MasterEntityTypeId": "369ed0fb-d317-4244-9f20-b7d834521e2b",
                "MasterEntityTypeName": "Timesheet",
                "MasterCustomDataTypeName": "Date",
                "MasterEntityValidationGroup": null
            }
        ]
    }
}
export const fetchPagePermissions = async (routeName: string, menuList: any, pageId: string, dispatch: any) => {
    let res = null;
    if (pageId && pageId !== '') {
        const foundItem = menuList.find((item: any) => item.SystemCategoryPageFrontEnd.toLowerCase() === routeName.toLowerCase());
        if (foundItem) {
            res = await dispatch(getPagePermissionsAsync(foundItem.SystemSecurityCategoryPageId));
            return res;
        }
    } else if (!pageId && menuList && menuList.length > 0) {
        const foundItem = menuList.find((item: any) => item.SystemCategoryPageFrontEnd.toLowerCase() === routeName.toLowerCase());
        if (foundItem) {
            res = await dispatch(getPagePermissionsAsync(foundItem.SystemSecurityCategoryPageId));
            return res;
        } else {
            return res;
        }
    }
};

export const getEmployeeNameWithCode = (ShowEmployeeNameBy: number, IsDisabled: boolean, EmployeeCode: string, LastName: string, FirstName: string) => {
    let EmployeeNameWithCode = '';

    if (ShowEmployeeNameBy === 2) {
        if (IsDisabled === true) {
            EmployeeNameWithCode = `${EmployeeCode ? `${EmployeeCode} - ` : ''}${LastName} ${FirstName} (Disabled)`;
        } else {
            EmployeeNameWithCode = `${EmployeeCode ? `${EmployeeCode} - ` : ''}${LastName} ${FirstName}`;
        }
    } else if (ShowEmployeeNameBy === 1) {
        if (IsDisabled === false) {
            EmployeeNameWithCode = `${EmployeeCode ? `${EmployeeCode} - ` : ''}${FirstName} ${LastName} (Disabled)`;
        } else {
            EmployeeNameWithCode = `${EmployeeCode ? `${EmployeeCode} - ` : ''}${FirstName} ${LastName}`;
        }
    } else {
        EmployeeNameWithCode = `${EmployeeCode ? `${EmployeeCode} - ` : ''}${FirstName} ${LastName}`;
    }

    return EmployeeNameWithCode;
}

export const isTimeEntryHoursFormat = (timeEntryHoursFormatId: any) => {
    if (timeEntryHoursFormatId === "074187eb-81d9-4e06-8e70-db7bc0c53784") {
        return "Time";
    } else if (timeEntryHoursFormatId === "ee9cb3b1-e1a1-4346-b9fc-a3da1c92a45e") {
        return "Decimal";
    } else if (timeEntryHoursFormatId === "cebf66d3-8374-4905-bf7e-665e36c5adae") {
        return "Percent";
    } else {
        return "None";
    }
}

export const setAttendanceStatus = (systemAttendanceTypeId: any) => {
    if (systemAttendanceTypeId == "d5e53b7f-3489-4359-9b8e-808143827629") {
        return { class: "badge-light-success", text: "Attendance" }
    }
    else {
        return { class: "badge-light-primary", text: "Break" }
    }
}

export const getSystemAttendanceTypeId = (SystemAttendanceType: any) => {
    switch (SystemAttendanceType) {
        case 'Attendance':
            return "D5E53B7F-3489-4359-9B8E-808143827629";
        case 'Break':
            return "36D3465F-B775-4E36-A00E-A3B5C3AE78D1";
        default:
            return "";
    }
}

export const formatTime = (date: string) => {
    let resTimeInString: string = '00:00';
    // Format the date as "HH:mm" method using js library moment
    if (!date) return resTimeInString;
    resTimeInString = moment(date) ? moment(date).format('HH:mm') : resTimeInString;
    return resTimeInString;
}

export const dateFormatByCulture = (date: any, culture: String) => {
    const dateObject = new Date(date);
    const isValidDate = !isNaN(dateObject.getTime());
    const hasT = date.includes("T");
    if (isValidDate && !hasT) {
        if (date.includes(" ")) {
            const formattedDate = format(date, culture.toString());
            return new Date(formattedDate);
        } else {
            return new Date(date);
        }
    } else if (isValidDate && hasT) {
        const formattedDate = format(date, "yyyy-MM-dd'T'HH:mm:ssXXX");
        return new Date(formattedDate);
    } else {
        //return parse(date, culture.toString(), new Date())
        let convertedDate = '';
        // const hasT = date.includes("T");
        hasT ? convertedDate = date.split('T')[0] : convertedDate = date.split(' ')[0];
        const parsedDate = parse(convertedDate, culture.toString(), new Date());
        const formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
        return new Date(formattedDate);
    }
}

export function FormatInvoiceDecimalNumber(
  locale: string,
  value: number,
  decimalPlaces: number
): string {

  if (value == null || isNaN(value)) return "";

  const finalLocale =
    locale && locale.trim() !== "" && locale !== "auto"
      ? locale
      : "en-US";

  return new Intl.NumberFormat(finalLocale + "-u-nu-latn", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(value);
}


/* export const formatDateWithDateAndMonthName = (date: any, locale: string = "en-US") => {
    //return moment(date).format("MMM DD, YYYY")
    if (!date) return "";
    
    const parsedDate = new Date(date);
      const finalLocale = locale && locale.trim() !== "" && locale !== "auto" ? locale : "en-US";
              //console.log( date,finalLocale,"formatDateWithDateAndMonthName")
        const gregorianLocale = `${finalLocale}-u-ca-gregory`;        

    // Guard against invalid date
    if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date value:", date);
        return "";
    }
      return new Intl.DateTimeFormat(
    gregorianLocale,
    {
      day: "numeric",
      month: "short",
      year: "numeric"
    }
  ).format(parsedDate);
} */

export const formatDateWithDateAndMonthName = (
    date: any,
    locale: string = "en-US"
) => {
    if (!date) return "";
    // const parsedDate = new Date(date);
    // Split work start
    // const datePart = date.includes("T")
    //     ? date.split("T")[0]
    //     : date;
    //   const parts = datePart.split("-");
    //   if (parts.length !== 3) return "";
    //   const year = Number(parts[0]);
    //   const month = Number(parts[1]); -1// JS months 0-based
    //   const day = Number(parts[2]);
    //   const parsedDate = new Date(year, month, day);
    // Split work end
    const parts = extractDateParts(date);
    if (!parts) return "";
    // const parsedDate = new Date(
    //     parts.y,
    //     parts.m,
    //     parts.d,
    // );
    const parsedDate = new Date(Date.UTC(
        parts.y,
        parts.m,
        parts.d
    ));
    if (isNaN(parsedDate.getTime())) return "";
    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
    const formatted = new Intl.DateTimeFormat(safeLocale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: 'UTC'
    }).format(parsedDate);
    return formatted.replace(/[\u200E\u200F\u061C]/g, "");
};

export const formatDateWithTime = (date: any, locale: string = "en-US") => {
    if (!date) return "";

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return "";

    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";

    // Force Gregorian calendar
    const gregorianLocale = `${finalLocale}-u-ca-gregory`;

    return new Intl.DateTimeFormat(gregorianLocale, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true
    }).format(parsedDate);
}

export function formatNumberCustomoriginalapiforclient(
    locale: string,
    value: string | null,
    decimalPlaces: number
): string {

    if (!value) return "";
    let str = String(value).trim();

    const isCommaSeparator = determineCommaSeprater(locale);
    let formattedRate = '';
    if (isCommaSeparator) {
        formattedRate = value.replace('.', ','); // Spanish: 6.50 → 6,50
        return formattedRate;
    } else if (isCommaSeparator === false) {
        formattedRate = value.replace(',', '.'); // US: 6,50 → 6.50
        return formattedRate;
    }

    return str;
}

export const validateCultureNumber = (value: string, culture: string, decimalPlaces: number): boolean => {
    value = value.trim();
    const isCommaSeparator = determineCommaSeprater(culture);
    if (isCommaSeparator) {
        const regex = /^[0-9]*([,][0-9]*)?$/;
        return regex.test(value);
    }

    const regex = /^[0-9]*([.][0-9]*)?$/;
    return regex.test(value);
};

interface FormatOptions {
    decimals?: number;
    decimalMark?: string;     // "," or "."
    thousand?: string;       // optional
}

export function parseLocaleNumber(str: string, locale: string) {
    const example = Intl.NumberFormat(locale).format(1.1);
    const decimalSeparator = example.charAt(1);

    // Normalize number for parseFloat
    const normalized = str
        .replace(new RegExp(`[^0-9${decimalSeparator}-]`, "g"), "") // remove everything except digits and decimal
        .replace(decimalSeparator, "."); // convert locale decimal → "."

    return parseFloat(normalized);
}
export function parseFlexibleNumber(input: string, locale: string): number | null {
    if (!input) return null;

    const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
    const localeDecimal = parts.find(p => p.type === "decimal")?.value || ".";
    const localeGroup = parts.find(p => p.type === "group")?.value || ",";

    let value = input.trim();

    // Remove group separators and spaces, but PRESERVE decimal separators
    if (localeGroup !== localeDecimal) {
        value = value.replace(new RegExp(`[${escapeRegex(localeGroup)}\\s]`, "g"), "");
    }

    // Find the last occurrence of the locale's decimal separator
    const lastDecimalIndex = value.lastIndexOf(localeDecimal);

    if (lastDecimalIndex !== -1) {
        // Split on the last decimal separator
        let intPart = value.substring(0, lastDecimalIndex).replace(new RegExp(escapeRegex(localeDecimal), "g"), "");
        let decPart = value.substring(lastDecimalIndex + 1).replace(new RegExp(escapeRegex(localeDecimal), "g"), "");
        value = intPart + "." + decPart;
    }

    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
}

function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
export const dateFormatByAccountCulture = (dateString: any, locale: string) => {
    // let cultureFormat = determineCultureFormat(locale);
    // if (isDateMatchingFormat(dateString, cultureFormat)) {
    //     const parsedDate = parse(dateString.split(' ')[0], cultureFormat, new Date());
    //     const formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
    //     return new Date(formattedDate);
    // } else {
    //     if (dateString.includes("/")) {
    //         const [day, month, year] = dateString.split('/');
    //         const dateObject = new Date(`${year}-${month}-${day}T00:00:00Z`);
    //         const formattedDate = dateObject.toISOString().replace(/\.\d{3}Z$/, "+00:00");
    //         return new Date(formattedDate);
    //     } else {
    //         const formattedDate = format(dateString, "yyyy-MM-dd'T'HH:mm:ssXXX");
    //         return new Date(formattedDate);
    //     }
    // }
    let cultureFormat = determineCultureFormat(locale);
    if (isDateMatchingFormat(dateString, cultureFormat)) {
        const parsedDate = parse(dateString.split(' ')[0], cultureFormat, new Date());
        const formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
        return new Date(formattedDate);
    } else {
        if (dateString.includes("/")) {
            const [day, month, year] = dateString.split('/');
            const dateObject = new Date(`${year}-${month}-${day}T00:00:00Z`);
            const formattedDate = dateObject.toISOString().replace(/\.\d{3}Z$/, "+00:00");
            return new Date(formattedDate);
        } else {
            // old
            // const formattedDate = format(dateString, "yyyy-MM-dd'T'HH:mm:ssXXX");
            // return new Date(formattedDate);
            // old

            // Try
            // const parsedDate = parseISO(dateString);
            // const timezoneOffset = parsedDate.getTimezoneOffset();
            // const correctedDate = addMinutes(parsedDate, timezoneOffset);
            // return new Date(format(correctedDate, "yyyy-MM-dd'T'HH:mm:ssXXX"));
            // Try

            if (dateString.includes("T")) {
                let [datePart, timePart] = dateString.split("T");
                // let [time, timezone] = timePart.split(/(?=[+-.])/);
                // let [hours, minutes, seconds] = time.split(":").map(Number);
                // let totalSeconds = hours * 3600 + minutes * 60 + seconds;
                // if (totalSeconds > 0) {
                //     dateString = `${datePart}T00:00:00${timezone}`;
                // }
                dateString = `${datePart}T00:00:00Z`;
            } else {
                let [datePart, timePart] = dateString.split(" ");
                // let [time, timezone] = timePart.split(/(?=[+-.])/);
                // let [hours, minutes, seconds] = time.split(":").map(Number);
                // let totalSeconds = hours * 3600 + minutes * 60 + seconds;
                // if (totalSeconds > 0) {
                //     dateString = `${datePart}T00:00:00${timezone}`;
                // } else {
                //     dateString = `${datePart}T00:00:00${getUTCOffset()}`;
                // }
                dateString = `${datePart}T00:00:00Z`;
            }
            const parsedDate = parseISO(dateString);
            const onlyDate = parsedDate.toISOString().split("T")[0];
            return parseISO(onlyDate);
        }
    }
}

export const dateFormatByAccountCultureForFilters = (dateString: any, locale: string) => {
    let cultureFormat = determineCultureFormat(locale);
    if (isDateMatchingFormat(dateString, cultureFormat)) {
        const parsedDate = parse(dateString.split(' ')[0], cultureFormat, new Date());
        const formattedDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
        return new Date(formattedDate);
    } else {
        if (dateString.includes("/")) {
            const [day, month, year] = dateString.split('/');
            const dateObject = new Date(`${year}-${month}-${day}T00:00:00Z`);
            const formattedDate = dateObject.toISOString().replace(/\.\d{3}Z$/, "+00:00");
            return new Date(formattedDate);
        } else {
            return parseISO(dateString);
        }
    }
}

function getUTCOffset() {
    const offsetMinutes = new Date().getTimezoneOffset();
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes > 0 ? "-" : "+";
    return `${sign}${String(hours).padStart(2, "0")}.${String(minutes).padStart(2, "0")}`;
}

export const determineDueStatus = (deadline: any, startDate: any) => {
    const now = new Date();
    let status: string = "Soon Due"
    if (deadline) {
        const deadlineDate = new Date(deadline);
        if (now > deadlineDate) {
            status = 'Overdue';
        } else if (now.getDate() === deadlineDate.getDate()) {
            status = 'Due';
        } else if (now < deadlineDate) {
            status = 'Soon Due';
        }
    } else if (startDate && now < new Date(startDate)) {
        status = 'Start Soon';
    }
    return status;
}

export const sortData = (dataArray: any, sortOrder: string, sortColumn: string) => {
    return dataArray.sort((a: any, b: any) => {
        let valueA = a[sortColumn] != null ? a[sortColumn] : "";
        let valueB = b[sortColumn] != null ? b[sortColumn] : "";
        if (sortColumn === 'IsDisabled') {
            valueA = (valueA === true || valueA === 'Yes') ? 1 : 0;
            valueB = (valueB === true || valueB === 'Yes') ? 1 : 0;
        } else if (sortColumn === 'BillingRate' || sortColumn === 'EmployeeRate' || sortColumn === 'HoursOff' || sortColumn === 'DayOff' || sortColumn === 'Amount'
            || sortColumn === 'TotalHours' || sortColumn === 'AllocationHours' || sortColumn === 'EarnedDay' || sortColumn === 'Earned' || sortColumn === 'ConsumeDay'
            || sortColumn === 'Consume' || sortColumn === 'CarryForwardDay' || sortColumn === 'CarryForward' || sortColumn === 'AvailableDay' || sortColumn === 'Available'
            || sortColumn === 'CompletedPercent' || sortColumn === 'WeekStartDay') {
            valueA = valueA;
            valueB = valueB;
        } else if (sortColumn === 'TeamMembers') {
            const count1 = (valueA.match(/data-tooltip-id="tooltip-Description"/g) || []).length;
            const count2 = (valueB.match(/data-tooltip-id="tooltip-Description"/g) || []).length;
            valueA = count1;
            valueB = count2;
        } else {
            valueA = valueA.toString().toLowerCase();
            valueB = valueB.toString().toLowerCase();
        }
        if (sortOrder === 'asc') {
            return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        } else {
            return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        }
    });
}

export const validateSystemIntegration = (systemIntegrationId: any) => {
    if (systemIntegrationId === "60398DE8-A493-4670-91FA-F38332264079") {
        return "Xero";
    } else if (systemIntegrationId === "B220ED0E-A33D-4110-A1C6-5C3244D79306") {
        return "Quickbooks";
    } else if (systemIntegrationId === "6E0E6A9C-7A37-4BDA-8D6A-D7D61F00B828") {
        return "OutlookCalendar";
    } else if (systemIntegrationId === "3B00775F-9B50-4A7D-926F-1192E2E3033E") {
        return "GoogleCalendar";
    } else {
        return "None";
    }
}

export const determineCultureFormatForTimesheet = (date: string, cultureInfoName: string) => {
    if (cultureInfoName === "en-US") {
        return moment(date).format("MMM DD, YYYY")//US
        // return "M/d/yyyy";//US
    } else if (cultureInfoName === "en-GB") {
        return moment(date).format("DD MMM, YYYY")//UK
        // return "dd/MM/yyyy";//UK
    } else if (cultureInfoName === "en-AU") {
        return moment(date).format("DD MMM, YYYY")//Australia
        // return "d/MM/yyyy";//Australia
    } else if (cultureInfoName === "en-NZ") {
        return moment(date).format("DD MMM, YYYY")//New Zealand
        // return "d/MM/yyyy";//New Zealand
    } else if (cultureInfoName === "en-SG") {
        return moment(date).format("DD MMM, YYYY")//Singapore
        // return "d/M/yyyy";//Singapore
    } else if (cultureInfoName === "en-ZA") {
        return moment(date).format("YYY MMM, DD")//SouthAfrica
        // return "yyyy-MM-dd";//SouthAfrica
    } else if (cultureInfoName === "en-MY") {
        return moment(date).format("DD MMM, YYYY")//Malaysia
        // return "d/M/yyyy";//Malaysia
    } else if (cultureInfoName === "en-IE") {
        return moment(date).format("DD MMM, YYYY")//Ireland
        // return "dd/MM/yyyy";//Ireland
    } else if (cultureInfoName === "en-IN") {
        return moment(date).format("DD MMM, YYYY")//India
        // return "dd-MM-yyyy";//India
    } else if (cultureInfoName === "en-HK") {
        return moment(date).format("DD MMM, YYYY")//Hong Kong
        // return "d/M/yy";//Hong Kong
    } else if (cultureInfoName === "en-029") {
        return moment(date).format("DD MMM, YYYY")//Caribbean
        // return "dd/MM/yyyy";//Caribbean
    } else if (cultureInfoName === "en-CA") {
        return moment(date).format("YYY MMM, DD")//Canada
        // return "yyyy-MM-dd";//Canada
    } else if (cultureInfoName === "en-BZ") {
        return moment(date).format("DD MMM, YYYY")//Belize
        // return "dd/MM/yyyy";//Belize
    } else if (cultureInfoName === "ar-SA") {
        return moment(date).format("DD MMM, YYYY")//Saudi Arabia
        // return "dd/MM/yy";//Saudi Arabia
    } else if (cultureInfoName === "gsw-FR") {
        return moment(date).format("DD MMM, YYYY")//France
        // return "dd/MM/yyyy";//France
    } else if (cultureInfoName === "zh-CN" || cultureInfoName === "	zh-TW") {
        return moment(date).format("YYY MMM, DD")//Chinese (China, Taiwan)
        // return "yyyy/M/d";//Chinese (China, Taiwan)
    } else if (cultureInfoName === "zh-SG" || cultureInfoName === "zh-HK" || cultureInfoName === "zh-MO") {
        return moment(date).format("DD MMM, YYYY")//Chinese (Hong Kong, Macao, Singapore)
        // return "d/M/yyyy";//Chinese (Hong Kong, Macao, Singapore)
    } else if (cultureInfoName === "nl-NL") {
        return moment(date).format("DD MMM, YYYY")//Dutch (Netherlands)
        // return "d-M-yyyy";//Dutch (Netherlands)
    } else if (cultureInfoName === "da-DK") {
        return "dd-MM-yyyy";//Danish (Denmark)
    } else if (cultureInfoName === "fi-FI") {
        return moment(date).format("DD MMM, YYYY")//Finnish (Finland)
        // return "d.M.yyyy";//Finnish (Finland)
    } else if (cultureInfoName === "fr-BE") {
        return moment(date).format("DD MMM, YYYY")//French (Belgium)
        // return "dd-MM-yy";//French (Belgium)
    } else if (cultureInfoName === "fr-CA") {
        return moment(date).format("YYY MMM, DD")//French (Canada)
        // return "yyyy-MM-dd";//French (Canada)
    } else if (cultureInfoName === "fr-FR" || cultureInfoName === "fr-LU" || cultureInfoName === "fr-MC" || cultureInfoName === "fr-MA") {
        return moment(date).format("DD MMM, YYYY")//French (France, Luxembourg, Monaco, Morocco)
        // return "dd/MM/yyyy";//French (France, Luxembourg, Monaco, Morocco)
    } else if (cultureInfoName === "fr-CH") {
        return moment(date).format("DD MMM, YYYY")//French (Switzerland)
        // return "dd.MM.yyyy";//French (Switzerland)
    } else if (cultureInfoName === "fy-NL") {
        return moment(date).format("DD MMM, YYYY")//Frisian (Netherlands)
        // return "d-M-yyyy";//Frisian (Netherlands)
    } else if (cultureInfoName === "nl-BE") {
        return moment(date).format("DD MMM, YYYY")//Dutch (Belgium)
        // return "d/MM/yyyy";//Dutch (Belgium)
    } else if (cultureInfoName === "de-DE" || cultureInfoName === "de-AT" || cultureInfoName === "de-LU" || cultureInfoName === "de-CH") {
        return moment(date).format("DD MMM, YYYY")//German
        // return "dd.MM.yyyy";//German
    } else if (cultureInfoName === "hi-IN") {
        return moment(date).format("DD MMM, YYYY")//Hindi (India)
        // return "dd-MM-yyyy";//Hindi (India)
    } else if (cultureInfoName === "it-IT") {
        return moment(date).format("DD MMM, YYYY")//Italian (Italy)
        // return "dd/MM/yyyy";//Italian (Italy)
    } else if (cultureInfoName === "pt-PT" || cultureInfoName === "pt-BR") {
        return moment(date).format("DD MMM, YYYY")//Portuguese (Portugal, Brazil)
        // return "dd/MM/yyyy";//Portuguese (Portugal, Brazil)
    } else if (cultureInfoName === "hu-HU") {
        return moment(date).format("YYY MMM, DD")//Hungarian (Hungary)
        // return "yyyy.MM.dd.";//Hungarian (Hungary)
    } else if (cultureInfoName === "nb-NO" || cultureInfoName === "nn-NO") {
        return moment(date).format("DD MMM, YYYY")//Norwegian, Bokmål & Nynorsk (Norway)
        // return "dd.MM.yyyy";//Norwegian, Bokmål & Nynorsk (Norway)
    } else if (cultureInfoName === "es-AR" || cultureInfoName === "es-BO" || cultureInfoName === "es-CO" || cultureInfoName === "es-MX" || cultureInfoName === "es-ES") {
        return moment(date).format("DD MMM, YYYY")//Spanish (Argentina, Bolivia, Colombia, Mexico, Spain)
        // return "dd/MM/yyyy";//Spanish (Argentina, Bolivia, Colombia, Mexico, Spain)
    } else if (cultureInfoName === "es-CL") {
        return moment(date).format("DD MMM, YYYY")//Spanish (Chile)
        // return "dd-MM-yyyy";//Spanish (Chile)
    } else if (cultureInfoName === "th-TH") {
        return moment(date).format("DD MMM, YYYY")//Thai (Thailand)
        // return "d/M/yyyy";//Thai (Thailand)
    } else {
        return moment(date).format("DD MMM, YYYY")
        // return "M/d/yyyy";
    }
}

export const determineCultureFormat = (cultureInfoName: string) => {
     const finalLocale =
    cultureInfoName && cultureInfoName.trim() !== "" && cultureInfoName !== "auto"
      ? cultureInfoName
      : "en-US";
  const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
  const sampleDate = new Date(2025, 11, 31);
  const formatted = new Intl.DateTimeFormat(safeLocale, { year: "numeric", month: "numeric", day: "numeric"}).format(sampleDate);
  const day = "dd";
  const month = "MM";
  const year = "yyyy";
  const pattern = formatted
    .replace(sampleDate.getFullYear().toString(), year)
    .replace((sampleDate.getMonth() + 1).toString().padStart(2, "0"), month)
    .replace(sampleDate.getDate().toString().padStart(2, "0"), day);

//   return pattern; 
  return pattern.replace(/[\u200E\u200F\u061C]/g, "");
    // if (cultureInfoName === "en-US") {
    //     return "M/d/yyyy";//US
    // } else if (cultureInfoName === "en-GB") {
    //     return "dd/MM/yyyy";//UK
    // } else if (cultureInfoName === "en-AU") {
    //     return "d/MM/yyyy";//Australia
    // } else if (cultureInfoName === "en-NZ") {
    //     return "d/MM/yyyy";//New Zealand
    // } else if (cultureInfoName === "en-SG") {
    //     return "d/M/yyyy";//Singapore
    // } else if (cultureInfoName === "en-ZA") {
    //     return "yyyy-MM-dd";//SouthAfrica
    // } else if (cultureInfoName === "en-MY") {
    //     return "d/M/yyyy";//Malaysia
    // } else if (cultureInfoName === "en-IE") {
    //     return "dd/MM/yyyy";//Ireland
    // } else if (cultureInfoName === "en-IN") {
    //     return "dd-MM-yyyy";//India
    // } else if (cultureInfoName === "en-HK") {
    //     return "d/M/yy";//Hong Kong
    // } else if (cultureInfoName === "en-029") {
    //     return "dd/MM/yyyy";//Caribbean
    // } else if (cultureInfoName === "en-CA") {
    //     return "yyyy-MM-dd";//Canada
    // } else if (cultureInfoName === "en-BZ") {
    //     return "dd/MM/yyyy";//Belize
    // } else if (cultureInfoName === "ar-SA") {
    //     return "dd/MM/yy";//Saudi Arabia
    // } else if (cultureInfoName === "gsw-FR") {
    //     return "dd/MM/yyyy";//France
    // } else if (cultureInfoName === "zh-CN" || cultureInfoName === "	zh-TW") {
    //     return "yyyy/M/d";//Chinese (China, Taiwan)
    // } else if (cultureInfoName === "zh-SG" || cultureInfoName === "zh-HK" || cultureInfoName === "zh-MO") {
    //     return "d/M/yyyy";//Chinese (Hong Kong, Macao, Singapore)
    // } else if (cultureInfoName === "nl-NL") {
    //     return "d-M-yyyy";//Dutch (Netherlands)
    // } else if (cultureInfoName === "da-DK") {
    //     return "dd-MM-yyyy";//Danish (Denmark)
    // } else if (cultureInfoName === "fi-FI") {
    //     return "DD.MM.yyyy";//Finnish (Finland)
    // } else if (cultureInfoName === "fr-BE") {
    //     return "dd-MM-yy";//French (Belgium)
    // } else if (cultureInfoName === "fr-CA") {
    //     return "yyyy-MM-dd";//French (Canada)
    // } else if (cultureInfoName === "fr-FR" || cultureInfoName === "fr-LU" || cultureInfoName === "fr-MC" || cultureInfoName === "fr-MA") {
    //     return "dd/MM/yyyy";//French (France, Luxembourg, Monaco, Morocco)
    // } else if (cultureInfoName === "fr-CH") {
    //     return "dd.MM.yyyy";//French (Switzerland)
    // } else if (cultureInfoName === "fy-NL") {
    //     return "dd-M-yyyy";//Frisian (Netherlands)
    // } else if (cultureInfoName === "nl-BE") {
    //     return "dd/MM/yyyy";//Dutch (Belgium)
    // } else if (cultureInfoName === "de-DE" || cultureInfoName === "de-AT" || cultureInfoName === "de-LU" || cultureInfoName === "de-CH") {
    //     return "dd.MM.yyyy";//German
    // } else if (cultureInfoName === "hi-IN") {
    //     return "dd-MM-yyyy";//Hindi (India)
    // } else if (cultureInfoName === "it-IT") {
    //     return "dd/MM/yyyy";//Italian (Italy)
    // } else if (cultureInfoName === "pt-PT" || cultureInfoName === "pt-BR") {
    //     return "dd/MM/yyyy";//Portuguese (Portugal, Brazil)
    // } else if (cultureInfoName === "hu-HU") {
    //     return "yyyy.MM.dd.";//Hungarian (Hungary)
    // } else if (cultureInfoName === "nb-NO" || cultureInfoName === "nn-NO") {
    //     return "dd.MM.yyyy";//Norwegian, Bokmål & Nynorsk (Norway)
    // } else if (cultureInfoName === "es-AR" || cultureInfoName === "es-BO" || cultureInfoName === "es-CO" || cultureInfoName === "es-MX" || cultureInfoName === "es-ES") {
    //     return "dd/MM/yyyy";//Spanish (Argentina, Bolivia, Colombia, Mexico, Spain)
    // } else if (cultureInfoName === "es-CL") {
    //     return "dd-MM-yyyy";//Spanish (Chile)
    // } else if (cultureInfoName === "th-TH") {
    //     return "d/M/yyyy";//Thai (Thailand)
    // } else {
    //     return "M/d/yyyy";
    // }

}

export const determineCultureDateTimeFormat = (cultureInfoName: string) => {
    if (cultureInfoName === "en-US") {
        return "M/d/yyyy h:mm:ss";
    } else if (cultureInfoName === "en-GB" || cultureInfoName === "en-IE") {
        return "dd/MM/yyyy HH:mm:ss";
    } else if (cultureInfoName === "en-IN" || cultureInfoName === "hi-IN") {
        return "dd-MM-yyyy HH:mm:ss";
    } else if (cultureInfoName === "en-CA" || cultureInfoName === "fr-CA") {
        return "yyyy-MM-dd HH:mm:ss";
    } else if (cultureInfoName === "de-DE" || cultureInfoName === "de-CH" || cultureInfoName === "de-AT" || cultureInfoName === "de-LU") {
        return "dd.MM.yyyy HH:mm:ss";
    } else if (cultureInfoName === "fr-FR" || cultureInfoName === "fr-LU") {
        return "dd/MM/yyyy HH:mm:ss";
    } else if (cultureInfoName === "nl-NL" || cultureInfoName === "fy-NL") {
        return "d-M-yyyy HH:mm:ss";
    } else if (cultureInfoName === "zh-CN" || cultureInfoName === "zh-HK") {
        return "yyyy/M/d HH:mm:ss";
    } else if (cultureInfoName === "es-ES" || cultureInfoName.startsWith("es-")) {
        return "dd/MM/yyyy HH:mm:ss";
    } else if (cultureInfoName === "hu-HU") {
        return "dd.MM.yyyy HH:mm:ss";
    } else {
        return "yyyy-MM-dd HH:mm:ss";
    }
}

export const isDateMatchingFormat = (dateString: string, dateFormat: string) => {
    // Step 1: Remove time component if present
    const dateOnly = dateString.toString().split(' ')[0];
    // Step 2: Detect separator (either "/" or "-")
    const separator = dateFormat.includes('/') ? '/' : dateFormat.includes('-') ? '-' : dateFormat.includes('.') ? '.' : null;
    if (!separator) return false;
    // Step 3: Split date string and format by the detected separator
    const dateParts = dateOnly.split(separator);
    const formatParts = dateFormat.split(separator);
    // Ensure we have exactly three parts for both date and format (e.g., dd/MM/yyyy)
    if (dateParts.length !== 3 || formatParts.length !== 3) return false;
    // Step 4: Create a mapping of format parts to date parts
    const formatMap: any = {};
    formatParts.forEach((part, index) => {
        formatMap[part] = dateParts[index];
    });
    // Step 5: Validate each part according to the format
    const day = formatMap['dd'] || formatMap['d'];
    const month = formatMap['MM'] || formatMap['M'];
    const year = formatMap['yyyy'] || formatMap['yy'];
    // Ensure day, month, and year are valid numbers
    if (!day || !month || !year) return false;
    // Convert day, month, and year to integers
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    // Validate month range (1-12)
    if (monthNum < 1 || monthNum > 12) return false;
    // Validate day range based on month and leap years
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > daysInMonth) return false;
    return true;
}

export const normalizeDateFormat = (format: string) => {
    format = format.replace(/\b(d)\b(?!d)/, 'dd');
    format = format.replace(/\b(M)\b(?!M)/, 'MM');
    format = format.replace(/\b(D)\b/, 'dd').replace(/\b(M)\b/, 'MM');
    return format;
}

// export const isValidDateFormat = (dateString: string, locale: string) => {
//     // Detect separator (`/` or `-`)
//     const separator = dateString.includes('/') ? '/' : dateString.includes('-') ? '-' : null;
//     if (!separator) return false; // Invalid if neither separator is present
//     // Split date string based on detected separator
//     const parts = dateString.split(separator);
//     if (parts.length !== 3) return false; // Ensure there are exactly 3 parts
//     // Parse date parts into numbers
//     const [month, day, year] = parts.map(part => parseInt(part, 10));
//     // Create a date formatter for the provided locale
//     const dateFormatter = new Intl.DateTimeFormat(locale, {
//         year: 'numeric',
//         month: 'numeric',
//         day: 'numeric'
//     });
//     // Create a Date object from the parts
//     const date = new Date(year, month - 1, day);
//     // Format date to string according to the locale
//     const formattedDate = dateFormatter.format(date);
//     // Check if formatted date matches original input format
//     return formattedDate === `${month}${separator}${day}${separator}${year}`;
// }


export const getFromResource = (values: string[], dispatch: any, t: any) => {
    const getTerminologyResponse: [] = dispatch(getManageTerminologyList());
    const processedValues = values.map((value) => {
        let getValue = validateEachTerminology(value, getTerminologyResponse);
        const processedValue = getValue !== undefined && getValue !== "" ? getValue : t(value);
        return { [value.split(" ").join("")]: processedValue };
    });
    return processedValues;
}

const validateEachTerminology = (value: string, list: any) => {
    let updatedValue;
    if (list && list.length > 0) {
        list.map((item: any) => {
            if (value.includes(item.TerminologyName)) {
                updatedValue = value.replace(item.TerminologyName, item.UserDefinedName);
            }
        });
        return updatedValue;
    } else {
        return "";
    }
}

export const getValueFromResource = (value: string, dispatch: any, t: any) => {
    if (value !== "") {
        let getValue = validateTerminology(value, dispatch);
        if (getValue != undefined && getValue !== "") {
            return getValue;
        } else {
            return t(value);
        }
        return t(value);
    } else {
        return value;
    }
}

// const validateTerminology = (value: string, dispatch: any) => {
//     const getTerminologyResponse: [] = dispatch(getManageTerminologyList());
//     let updatedValue;
//     if (getTerminologyResponse && getTerminologyResponse.length > 0) {
//         getTerminologyResponse.map((item: any) => {
//             if (value.includes(item.TerminologyName)) {
//                 updatedValue = value.replace(item.TerminologyName, item.UserDefinedName);
//             }
//         });
//         return updatedValue;
//     } else {
//         return "";
//     }
// }
const validateTerminology = (value: string, dispatch: any) => {
    const getTerminologyResponse: any[] = dispatch(getManageTerminologyList());
    if (getTerminologyResponse && getTerminologyResponse.length > 0) {
        let updatedValue = value;
        getTerminologyResponse.map((item: any) => {
            // if (updatedValue.includes(item.TerminologyName)) {
            //     updatedValue = updatedValue.replace(new RegExp(item.TerminologyName, "g"), item.UserDefinedName);
            // }
            if (updatedValue && typeof updatedValue === "string" && updatedValue.includes(item.TerminologyName)) {
                updatedValue = updatedValue.replace(new RegExp(item.TerminologyName, "g"), item.UserDefinedName);
            }
        });
        return t(updatedValue);
    }
};

export const callServices = async (accessToken: string) => {
    const response = await fetchAPI('Services/react.svc/GetLoginFromReact', 'POST', { AccessToken: accessToken }, false);
    if (response.ok) {
        const text = await response.text();
        const startIndex = text.indexOf('[');
        const endIndex = text.lastIndexOf(']') + 1;
        if (startIndex !== -1 && endIndex !== -1) {
            const jsonText = text.substring(startIndex, endIndex);
            const fixedJSONString = jsonText.replace(/\\"/g, '"');
            const parsedArray = JSON.parse(fixedJSONString);
            if (parsedArray[0].message === "success") {
                window.location.href = `${window.origin}/classic/Employee/Default.aspx`;
            } else {
                ShowToast('Warning', parsedArray[0].message, "warning", "toast-top-right");
                return true;
            }
        }
    } else {
        ShowToast('Error', "Something went wrong", "warning", "toast-top-right");
        return true;
    }
}

export const determineCommaSeprater = (cultureInfoName: string) => {

    // let array: Array<string>
    // array = ['de-DE', 'de-AT', 'de-LU', 'fr-FR', 'fr-LU', 'fr-MC', 'fr-MA', 'it-IT', 'es-MX', 'es-CO', 'es-BO', 'es-AR', 'es-ES', 'nl-NL', 'fy-NL', 'pt-BR',
    //     'pt-PT', 'ru-RU', 'sv-SE', 'id-ID', 'tr-TR'];
    // if (array.includes(cultureInfoName)) {
    //     return true;
    // } else {
    //     return false;
    // }
        const finalLocale =
        cultureInfoName && cultureInfoName.trim() !== "" && cultureInfoName !== "auto"
            ? cultureInfoName
            : "en-US";
    const safeLocale = `${finalLocale}-u-nu-latn`;
    const formatted = new Intl.NumberFormat(safeLocale).format(1.1);
    const match = formatted.match(/[^0-9]/);
    return match ? match[0] === "," : false;
}
/* export const determineCommaSeprater = (cultureInfoName?: string): boolean => {
    // Detect browser language
    const browserLang = (navigator.languages?.[0] || navigator.language || "").toLowerCase();

    // Decide which culture to use: browserLang if ES or FR, otherwise fallback to cultureInfoName
    const culture = (browserLang.startsWith("es") || browserLang.startsWith("fr"))
        ? browserLang
        : (cultureInfoName ? cultureInfoName.toLowerCase() : browserLang);
    // Full culture codes list
    const fullCodes = [
        'de-de','de-at','de-lu','fr-fr','fr-lu','fr-mc','fr-ma','it-it',
        'es-mx','es-co','es-bo','es-ar','es-es','nl-nl','fy-nl','pt-br',
        'pt-pt','ru-ru','sv-se','id-id','tr-tr'
    ];

    const shortCodes = fullCodes.map(c => c.split('-')[0]);
    const shortCulture = culture.split('-')[0];
    if (fullCodes.includes(culture)) return true;
    if (shortCodes.includes(shortCulture)) return true;
    return false;
}; */
export function formatHoursOff(value: number, decimalPlaces: number, locale: string) {
    // if (value == null || isNaN(value)) return "";

    // // let rounded = Number(value).toFixed(decimalPlaces);

    // // rounded = rounded.replace(/\.?0+$/, "");

    // // const isCommaSeparator = determineCommaSeprater(cultureInfoName);
    // // if (isCommaSeparator) {
    // //     rounded = rounded.replace('.', ',');
    // // }

    // // return rounded;
    // let formatted = value.toFixed(decimalPlaces);

    // const isCommaSeparator = determineCommaSeprater(cultureInfoName);

    // if (isCommaSeparator) {
    //     formatted = formatted.replace('.', ',');
    // }

    // return formatted;

  if (value == null || isNaN(value)) return "";

  const finalLocale =
    locale && locale.trim() !== "" && locale !== "auto"
      ? locale
      : "en-US";

  return new Intl.NumberFormat(finalLocale + "-u-nu-latn", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(value);
}

interface FormatOptions {
    decimals?: number;
    locale?: string; // e.g., "en-US", "es-ES"
}

export function formatNumberCustomoriginalapiv2(locale: string, value: any | null, decimalPlaces: number): string {
    // // Handle null/undefined/empty
    if (!value && value !== 0) return "";

    // Convert to string and trim
    let str = String(value).trim();

    const isCommaSeparator = determineCommaSeprater(locale);

    // Parse the number (handle both . and , as decimal separators from API)
    const numericValue = parseFloat(str.replace(',', '.'));

    // Check if valid number
    if (isNaN(numericValue)) return str;

    // Format based on locale
    if (isCommaSeparator) {
        // Spanish Argentina: comma as decimal separator
        return numericValue.toFixed(decimalPlaces).replace('.', ',');
    }
    else if (isCommaSeparator === false) {
        // US English: dot as decimal separator
        return numericValue.toFixed(decimalPlaces);
    }

    // Default
    return numericValue.toFixed(decimalPlaces);
}

export function formatNumberCustomoriginalapi(
    locale: string,
    value: string | null,
    decimalPlaces: number
): string {

    if (!value) return "";
    let str = String(value).trim();
    let formattedRate = '';
    if (locale === 'es-AR') {
        formattedRate = value.replace('.', ',');
        return formattedRate;
    } else if (locale === 'en-US') {
        formattedRate = value.replace(',', '.');
        return formattedRate;
    }

    return str;
}

export function formatNumberCustomoriginal(
    locale: string,
    value: any,
    decimalPlaces: number
): string {

    const isCommaSeparator = determineCommaSeprater(locale.toString());
    const normalizedValue = value.replace(',', '.');
    const parsed = parseFloat(normalizedValue);

    if (isNaN(parsed)) return isCommaSeparator === false ? '' : '';

    let formatted = parsed.toFixed(decimalPlaces);

    if (isCommaSeparator) {
        formatted = formatted.replace('.', ',');
    }

    return formatted;
//      if (value == null || isNaN(value)) return "";

//   const finalLocale =
//     locale && locale.trim() !== "" && locale !== "auto"
//       ? locale
//       : "en-US";

//   return new Intl.NumberFormat(finalLocale + "-u-nu-latn", {
//     minimumFractionDigits: decimalPlaces,
//     maximumFractionDigits: decimalPlaces
//   }).format(value);
}

export function formatNumberCustom(
    locale: string,
    value: string | null,
    decimalPlaces: number
): string {
 
  if (!value) return "";
  let str = String(value).trim();
   if (locale === "es-AR" || locale === "es-FR") {
    str = str.replace(/\./g, "");   
    str = str.replace(",", ".");    
  } else {
    str = str.replace(/,/g, "");
  }
 
  let num = Number(str);
  if (isNaN(num)) return value;
 
  let formatted = num.toFixed(decimalPlaces); 
 
  if (locale === "es-AR") {
    formatted = formatted.replace(".", ","); 
  }
 
  return formatted;
}

// Helper function to parse localized number input
export const parseLocalizedNumber = (value: string | number): number | null => {
    if (typeof value === 'number') return value;
    if (!value) return 0;

    // Remove any whitespace
    const cleaned = value.toString().trim();

    // Replace comma with dot for decimal separator
    const normalized = cleaned.replace(',', '.');

    // Parse the number
    const parsed = parseFloat(normalized);

    return isNaN(parsed) ? null : parsed;
};


interface FormatOptions {
    decimals?: number;
    decimalMark?: string;
    thousand?: string;
}

export function formatNumber(value: number, options: FormatOptions) {
    if (value === null || value === undefined || isNaN(value)) return "";

    let { decimals, decimalMark } = options;
    let formatted = value.toFixed(decimals);

    if (decimalMark === ",") {
        formatted = formatted.replace(".", ",");
    }
    return formatted;
}

export const parseByCulture = (value: any, culture: string): number => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;

    let str = String(value);

    const isCommaSeparator = determineCommaSeprater(culture);
    if (isCommaSeparator) {
        str = str.replace(/\./g, '').replace(',', '.');
    }

    return parseFloat(str) || 0;
};

export const parseCultureNumber = (value: any, culture: string): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;

    let str = String(value).trim();

    str = str.replace(/\s/g, '');

    if (determineCommaSeprater(culture)) {
        str = str.replace(/\./g, '');

        if (str.endsWith(',')) {
            str = str + '0';
        }

        str = str.replace(',', '.');
    }

    if (!/^[-+]?\d*(\.\d+)?$/.test(str)) {
        return 0;
    }

    return Number(str);
};

export const getBrowserCulture = () => {
    return (
        navigator.languages?.[0] ||
        navigator.language ||
        "en-US"
    );
};

// export const formatCurrntDateTime = (date: any, locale: string = "en-US") => {
//     console.log('date',date)
//     if (!date) return "";
//     const parsedDate = new Date(date);
//     if (isNaN(parsedDate.getTime())) return "";
//     const finalLocale =
//         locale && locale.trim() !== "" && locale !== "auto"
//             ? locale
//             : "en-US";
//     const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
//     const formatted = new Intl.DateTimeFormat(safeLocale, {
//         day: "2-digit",
//         month: "2-digit",
//         year: "numeric",
//         hour: "2-digit",
//         minute: "2-digit",
//         hour12: false
//     }).format(parsedDate);
//     return formatted.replace(/[\u200E\u200F\u061C]/g, "");
// };

export const formatCurrntDateTime = (
    date: any,
    locale: string = "en-US"
) => {
    if (!date) return "";

    let safeDate: Date | null = null;
    if (date instanceof Date) {
        if (isNaN(date.getTime())) return "";
        safeDate = new Date(
            Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                date.getHours(),
                date.getMinutes()
            )
        );
    }
    else {
        const raw = date.toString().trim();
        const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
        if (!match) return "";
        const [            ,
            year,
            month,
            day,
            hour = "0",
            minute = "0"
        ] = match;

        safeDate = new Date(
            Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hour),
                Number(minute)
            )
        );
    }
    if (!safeDate || isNaN(safeDate.getTime())) return "";
    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;

    const formatted = new Intl.DateTimeFormat(safeLocale, {
        timeZone: "UTC",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
    }).format(safeDate);
    return formatted.replace(/[\u200E\u200F\u061C]/g, "");
};


export const getDateFormatPatternByCulture = (locale: string = "en-US") => {
  const finalLocale =
    locale && locale.trim() !== "" && locale !== "auto"
      ? locale
      : "en-US";
  const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
  const sampleDate = new Date(2025, 11, 31);
  const formatted = new Intl.DateTimeFormat(safeLocale, { year: "numeric", month: "numeric", day: "numeric"}).format(sampleDate);
  const day = "dd";
  const month = "MM";
  const year = "yyyy";
  const pattern = formatted
    .replace(sampleDate.getFullYear().toString(), year)
    .replace((sampleDate.getMonth() + 1).toString().padStart(2, "0"), month)
    .replace(sampleDate.getDate().toString().padStart(2, "0"), day);
  return pattern;
};

export const getDecimalSeparator = (locale: string = "en-US"): string => {
    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    const safeLocale = `${finalLocale}-u-nu-latn`;
    const formatted = new Intl.NumberFormat(safeLocale).format(1.1);
    const match = formatted.match(/[^0-9]/);
    return match ? match[0] : ".";
};

export function FormatInvoiceDecimalNumberWithTwoDigitInteger(
    locale: string,
    value: number,
    decimalPlaces: number
): string {
    if (value == null || isNaN(value)) return "";
    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    return new Intl.NumberFormat(finalLocale + "-u-nu-latn", {
        minimumIntegerDigits: 2,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
    }).format(value);
}

export function FormatNumberCultureAware(
    locale: string,
    value: number
): string {
    if (value == null || isNaN(value)) return "";

    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    const formatted = new Intl.NumberFormat(finalLocale + "-u-nu-latn", {
        useGrouping: false,
        maximumFractionDigits: 20
    }).format(value);

    return formatted;
}

export const getFormattedCustomField = (customFieldsArray: any[], fieldName: string, value: any) => {
    const field = customFieldsArray.find((item: any) => item.DatabaseFieldName === fieldName);
    if (field && field.MasterCustomDataTypeName === 'Number' && value) {
        return value.toString().replace(',', '.');
    }
    return value;
} 
export const getAndSetCustomFieldValues = (customField: string, customFieldData: any, customFields: any, cultureInfoName: any, decimalPlaces: any) => {
    const field = customFields.find(
        (item: any) => item.DatabaseFieldName === customField
    );
    if (!field || customFieldData === null || customFieldData === undefined || customFieldData === '') {
        return customFieldData;
    }
    switch (field.MasterCustomDataTypeName) {
        case 'Number':
            if (customFieldData.toString() === 'NaN' || isNaN(parseFloat(customFieldData))) {
                return '';
            }
            return FormatInvoiceDecimalNumber(
                cultureInfoName.toString(),
                customFieldData,
                decimalPlaces
            );
        case 'Date':
            return formatDateWithDateAndMonthName(
                customFieldData,
                cultureInfoName.toString()
            );
        default:
            return customFieldData;
    }
};

export const getCurrentDateInTimeZone = (timeZone: string): Date => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const [{ value: month }, , { value: day }, , { value: year }] = formatter.formatToParts(now);
    return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
}

// const extractDateParts = (input: any): { y: number; m: number; d: number } | null => {
//   if (!input) return null;
//   //Case 1: Date object
//   if (input instanceof Date) {
//     if (isNaN(input.getTime())) return null;
//     return {
//       y: input.getFullYear(),
//       m: input.getMonth(),
//       d: input.getDate()
//     };
//   }
//   //Convert everything else to string
//   const str = String(input);
//   //Case 2: ISO / datetime → take date part
//   const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
//   if (match) {
//     return {
//       y: Number(match[1]),
//       m: Number(match[2]) - 1,
//       d: Number(match[3])
//     };
//   }
//   //Case 3: JS date string (Mon Jan 26 2026 ...)
//   const temp = new Date(str);
//   if (!isNaN(temp.getTime())) {
//     return {
//       y: temp.getFullYear(),
//       m: temp.getMonth(),
//       d: temp.getDate()
//     };
//   }
//   return null;
// };

const extractDateParts = (
    input: any
): { y: number; m: number; d: number } | null => {
    if (!input) return null;
    // Date object ALWAYS use UTC parts
    if (input instanceof Date) {
        if (isNaN(input.getTime())) return null;
        return {
            y: input.getUTCFullYear(),
            m: input.getUTCMonth(),
            d: input.getUTCDate()
        };
    }
    const str = String(input).trim();
    // ISO / SQL datetime → extract YYYY-MM-DD safely
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return {
            y: Number(match[1]),
            m: Number(match[2]) - 1, // JS month is 0-based
            d: Number(match[3])
        };
    }
    return null;
};

export const toUtcDate = (date: any) => {
    if (!date) return null;
    const parts = extractDateParts(date);
    
    if (!parts) return null;
    return new Date(Date.UTC(parts.y, parts.m, parts.d));
};

// export const formatDateWithTZ = (date: any, locale = "en-US", timeZone = "UTC") => {
//     var utcDate = toUtcDate(date);
//     if (!utcDate) return "";
//     const parsedDate = new Date(utcDate);
//     if (isNaN(parsedDate.getTime())) return "";
//     const finalLocale =
//         locale && locale.trim() !== "" && locale !== "auto"
//             ? locale
//             : "en-US";
//     const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
//     const formatted = new Intl.DateTimeFormat(safeLocale, {
//         year: "numeric",
//         month: "2-digit",
//         day: "2-digit",
//         timeZone
//     }).format(parsedDate);
//     return formatted.replace(/[\u200E\u200F\u061C]/g, "");
// };

export const formatDateWithTZ = (
    date: any,
    locale = "en-US",
    timeZone = "UTC"
) => {
    if (!date) return "";
    // Use the full ISO date directly
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return "";
    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";
    const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;
    const formatted = new Intl.DateTimeFormat(safeLocale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone
    }).format(parsedDate);
    return formatted.replace(/[\u200E\u200F\u061C]/g, "");
};

export const formatDateTimeWithTZ = (
    date: any,
    locale = "en-US",
    timeZone = "UTC"
) => {
    const utcDate = toUtcDate(date);
    if (!utcDate) return "";

    const parsedDate = new Date(utcDate);
    if (isNaN(parsedDate.getTime())) return "";

    const finalLocale =
        locale && locale.trim() !== "" && locale !== "auto"
            ? locale
            : "en-US";

    const safeLocale = `${finalLocale}-u-ca-gregory-nu-latn`;

    const formatted = new Intl.DateTimeFormat(safeLocale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone
    }).format(parsedDate);

    return formatted.replace(/[\u200E\u200F\u061C]/g, "");
};

 export const getSystemTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

export const isAccountFreeOrExpired = (licenseFlag: string) => {
    if (licenseFlag == 'true') {
        return 'Free Account'
    } else {
        return 'Trial Account'
    }
};

export const getStatusConfig = (status?: string) => {
    const map: Record<string, { label: string; bg: string; color: string; dot: string; }> = {
        present: {label: 'Present',bg: '#EAF3DE',color: '#3B6D11',dot: '#639922'},
        clockedin: {label: 'Clocked In',bg: '#EAF3DE',color: '#3B6D11',dot: '#639922'},
        clockedout: {label: 'Clocked Out',bg: '#FCEBEB',color: '#A32D2D',dot: '#E24B4A'},
        onbreak: {label: 'On Break',bg: '#FAEEDA',color: '#BD6E0D',dot: '#EF9F27'},
        absent: {label: 'Absent',bg: '#FCEBEB',color: '#A32D2D',dot: '#E24B4A'},
        'short day': {label: 'Short Day',bg: '#FAEEDA',color: '#854F0B',dot: '#EF9F27'},
        'half day': {label: 'Half Day',bg: '#E6F8F4',color: '#3CBA9F',dot: '#2A9D84'},
        weekend: {label: 'Weekend',bg: '#F1EFE8',color: '#5F5E5A',dot: '#888780'},
        'non working day': {label: 'Non Working Day',bg: '#E6F1FB',color: '#185FA5',dot: '#378ADD'},
        custom: {label: 'Custom',bg: '#F1EFE8',color: '#5F5E5A',dot: '#888780'},
        holiday: {label: 'Holiday',bg: '#E6F1FB',color: '#185FA5',dot: '#378ADD'},
        partial_day: {label: 'Partial Day',bg: '#FAEEDA',color: '#854F0B',dot: '#EF9F27'},
        submitted: {label: 'Submitted',bg: '#E6F1FB',color: '#185FA5',dot: '#378ADD'},
        approved: {label: 'Approved',bg: '#EAF3DE',color: '#3B6D11',dot: '#639922'},
        rejected: {label: 'Rejected',bg: '#FCEBEB',color: '#A32D2D',dot: '#E24B4A'},
    };

    return (
        map[status?.toLowerCase() || ''] || {
            label: status || '—',
            bg: '#F1EFE8',
            color: '#5F5E5A',
            dot: '#888780',
        }
    );
};